<script lang="ts">
  import type { Snippet } from 'svelte'
  import { createAttachmentKey, type Attachment } from 'svelte/attachments'
  import { cubicOut } from 'svelte/easing'
  import { clampToViewport } from 'frontend/inertia/attachments/clampToViewport'
  import { focusTrap } from 'frontend/inertia/attachments/focusTrap'
  import { down } from 'shared/javascripts/util/breakpoints'
  import { setFlyoutContext } from './flyout'
  import type { FlyoutTriggerProps } from './flyout'
  import type { TransitionConfig } from 'svelte/transition'

  let {
    label,
    triggerText,
    trigger,
    children,
    placement = 'down',
    align = 'left',
    caret = false,
    mobileFullscreen = false,
    openOnHover = false,
    disabled = false,
    onIntent,
    onOpen,
    onClose,
    onDismiss,
    triggerElement = $bindable(),
  }: {
    label: string
    triggerText?: Snippet<[{ open: boolean }]>
    trigger?: Snippet<[FlyoutTriggerProps]>
    children: Snippet<[{ close: () => void }]>
    placement?: 'up' | 'down'
    align?: 'left' | 'center' | 'right'
    caret?: boolean
    mobileFullscreen?: boolean
    openOnHover?: boolean
    disabled?: boolean
    onIntent?: () => void
    onOpen?: () => void
    onClose?: () => void
    onDismiss?: () => void
    triggerElement?: HTMLElement
  } = $props()

  let open = $state(false)
  let hoverOpened = $state(false)
  let pointerOpened = $state(false)
  let levelCount = $state(0)

  const focusOptions: FocusOptions | undefined = $derived(
    pointerOpened ? { focusVisible: false } : undefined,
  )
  let hoverCloseTimeout: ReturnType<typeof setTimeout> | undefined

  $effect(() => () => clearTimeout(hoverCloseTimeout))

  // A stable key, so the attachment is not torn down and re-run whenever `triggerProps` recomputes.
  const trackTrigger = createAttachmentKey()

  const triggerProps: FlyoutTriggerProps['props'] = $derived({
    type: 'button',
    disabled,
    'aria-label': label,
    'aria-haspopup': 'true',
    'aria-expanded': open,
    onclick: toggle,
    [trackTrigger]: (node: HTMLElement) => {
      triggerElement = node
      return () => {
        triggerElement = undefined
      }
    },
  })

  setFlyoutContext({
    get open() {
      return open
    },
    get hoverOpened() {
      return hoverOpened
    },
    get focusOptions() {
      return focusOptions
    },
    get levelCount() {
      return levelCount
    },
    register() {
      levelCount += 1
      return () => {
        levelCount -= 1
      }
    },
  })

  function show(byHover = false, byPointer = false): void {
    if (open || disabled) {
      return
    }
    onIntent?.()
    hoverOpened = byHover
    pointerOpened = byPointer
    open = true
    onOpen?.()
  }

  function close(): void {
    if (!open) {
      return
    }
    open = false
    onClose?.()
  }

  function dismiss(): void {
    if (!open) {
      return
    }
    close()
    onDismiss?.()
  }

  // A click from Enter or Space has `detail` 0.
  function toggle(event: MouseEvent): void {
    if (open) {
      dismiss()
    } else {
      show(false, event.detail > 0)
    }
  }

  // Browsers fire a synthetic mouseenter on tap, which would fight the click handler over the same state.
  function isHoverCapable(): boolean {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches
  }

  function isMobileMenuLayout(): boolean {
    return window.matchMedia(down('header-medium')).matches
  }

  function onMouseEnter(): void {
    if (openOnHover && isHoverCapable()) {
      clearTimeout(hoverCloseTimeout)
      show(true)
    } else {
      onIntent?.()
    }
  }

  function onMouseLeave(): void {
    if (!openOnHover || !isHoverCapable()) {
      return
    }
    hoverCloseTimeout = setTimeout(dismiss, 100)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- a css transition needs no node
  function flyoutMotion(node: HTMLElement): TransitionConfig {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return { duration: 0 }
    }

    const shift = placement === 'up' ? -6 : 6
    return {
      duration: 220,
      easing: cubicOut,
      css: (t, u) =>
        `opacity: ${t}; transform: translateX(var(--flyout-shift-x, 0)) translateY(${u * shift}px)`,
    }
  }

  const dismissFromDocument: Attachment<HTMLElement> = (node) => {
    function onClick(event: MouseEvent): void {
      if (!node.contains(event.target as Node)) {
        dismiss()
      }
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape' || !open) {
        return
      }
      const focusWasInside = node.contains(document.activeElement)
      dismiss()
      if (focusWasInside) {
        triggerElement?.focus()
      }
    }

    document.addEventListener('click', onClick, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="flyout"
  {@attach dismissFromDocument}
  onmouseenter={onMouseEnter}
  onmouseleave={onMouseLeave}
>
  {#if trigger}
    {@render trigger({ open, props: triggerProps })}
  {:else}
    <button class="trigger" {...triggerProps}>
      {@render triggerText?.({ open })}
    </button>
  {/if}

  {#if open}
    <div
      class="panel"
      class:-up={placement === 'up'}
      class:-center={align === 'center'}
      class:-right={align === 'right'}
      class:-caret={caret}
      class:-fullscreen={mobileFullscreen}
      role="dialog"
      aria-label={label}
      tabindex="-1"
      {@attach focusTrap(!hoverOpened, focusOptions)}
      {@attach mobileFullscreen && isMobileMenuLayout() ? undefined : clampToViewport()}
      transition:flyoutMotion
    >
      {@render children({ close })}
    </div>
  {/if}
</div>

<style>
  .flyout {
    position: relative;
    flex: none;
  }

  .trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    cursor: pointer;
  }

  .trigger:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .panel {
    --flyout-item-padding: var(--space-3) var(--space-4);
    --flyout-item-lead-width: 1.55em;
    --flyout-item-gap: var(--space-4);
    --flyout-item-border-bottom: 1px solid var(--color-border);
    --flyout-item-color: var(--color-text);
    --flyout-item-color-disabled: var(--color-text-muted);
    --flyout-item-hover-background-color: var(--color-primary-bright);
    --flyout-item-transition: background-color 0.15s var(--ease);

    position: absolute;
    top: calc(100% + var(--flyout-gap, var(--space-2)));
    left: 0;
    z-index: 30;
    width: var(--flyout-width, min(325px, calc(100dvw - 2 * var(--page-margin, 16px))));
    padding: var(--flyout-padding, var(--space-2) 0);
    border-radius: var(--flyout-radius, var(--border-radius-lg));
    overflow: var(--flyout-overflow, hidden);
    background-color: var(--color-white);
    box-shadow: var(--flyout-shadow, 0 0 20px rgb(0 0 0 / 10%));
    outline: none;
  }

  .panel.-up {
    top: auto;
    bottom: calc(100% + var(--flyout-gap, var(--space-2)));
  }

  .panel.-center {
    --flyout-shift-x: -50%;
    left: 50%;
    transform: translateX(var(--flyout-shift-x));
  }

  .panel.-right {
    left: auto;
    right: 0;
  }

  .panel.-caret::before {
    content: '';
    position: absolute;
    top: -7px;
    left: 50%;
    width: 14px;
    height: 14px;
    translate: -50%;
    background-color: var(--color-white);
    transform: rotate(45deg);
    box-shadow: -2px -2px 5px rgb(0 0 0 / 4%);
  }

  .panel.-caret.-right::before {
    left: auto;
    right: 40px;
    margin-left: 0;
  }

  @media (--header-medium-down) {
    .panel.-fullscreen {
      --flyout-shift-x: 0;
      position: fixed;
      top: 0;
      bottom: auto;
      left: 0;
      right: 0;
      z-index: 40;
      width: 100%;
      transform: none;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    .panel.-fullscreen::before {
      display: none;
    }
  }
</style>
